// SVG-based Certificate Generator for Aureus Alliance Holdings
// Generates certificates using the official SVG template with KYC data integration

import QRCode from 'qrcode';
import { getServerSessionId } from './serverSession';

// API helper for certificate operations - NO getServiceRoleClient() in browser
async function callCertificateAPI(action: string, params: Record<string, any> = {}): Promise<any> {
 const sessionId = getServerSessionId();
 const headers: Record<string, string> = { 'Content-Type': 'application/json' };
 if (sessionId) {
 headers['X-Session-Id'] = sessionId;
 }
 const response = await fetch('/api/admin/certificate-service', {
 method: 'POST',
 headers,
 body: JSON.stringify({ action, ...params })
 });
 return response.json();
}

export interface CertificateData {
 certificateNumber: string; // Keep for backward compatibility
 certNo: string; // New sequential certificate number (e.g., "0-000-001")
 userName: string;
 userIdNumber: string;
 userAddress: {
 line1: string;
 line2: string;
 city: string;
 country: string;
 postalCode: string;
 };
 sunId: string;
 userId: number; // Numeric user ID for per-user layout overrides
 sharesCount: number;
 issueDate: string;
 purchaseDate: string;
}

export interface CertificateTemplateElement {
 id: string;
 element_id: string;
 element_type: 'text' | 'image' | 'line' | 'rectangle' | 'watermark';
 template_version: string;
 x_position: number;
 y_position: number;
 width?: number;
 height?: number;
 font_family?: string;
 font_size?: number;
 font_weight?: string;
 font_color?: string;
 text_anchor?: string;
 static_content?: string;
 data_field?: string;
 is_dynamic: boolean;
 opacity?: number;
 rotation?: number;
 z_index?: number;
 description?: string;
 is_active: boolean;
}

export interface KYCData {
 full_legal_name?: string;
 first_name?: string;
 last_name?: string;
 id_type?: string;
 id_number_encrypted?: string;
 street_address?: string;
 city?: string;
 province?: string; // State/region/province field
 suburb?: string; // Alternative name for region
 country_name?: string;
 postal_code?: string;
 email_address: string;
}

export class SVGCertificateGenerator {
 private svgTemplate: string = '';
 private backgroundImage: HTMLImageElement | null = null;
 private backgroundLoaded: boolean = false;

	 // Certificate asset URLs. Local /public assets are the production default so certificate
	 // generation does not depend on optional Supabase template objects being present.
	 private static readonly LOCAL_TEMPLATE_URL = '/certificates/certificate-template.svg';
	 private static readonly LOCAL_PNG_BACKGROUND_URL = '/certificates/certificate_background.png';
	 private static readonly REMOTE_TEMPLATE_URL = 'https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/Aureus_Template.svg';
	 private static readonly REMOTE_PNG_BACKGROUND_URL = 'https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/certificate_background.png';
	 private static readonly CUSTOM_TEMPLATE_FLAG = 'aureusCertificateCustomTemplateEnabled';
		 private static readonly CUSTOM_TEMPLATE_URL_KEY = 'aureusCertificateCustomTemplateUrl';
 // SVG viewBox dimensions (used for coordinate mapping)
 private static readonly SVG_VIEWBOX_WIDTH = 842.25;
 private static readonly SVG_VIEWBOX_HEIGHT = 595.5;

 constructor() {
 this.loadTemplate();
 this.loadBackgroundImage();
 }

 /**
	 * Load the SVG template.
	 * Default path is the bundled public asset. A custom Supabase template is only
	 * attempted after an admin explicitly uploads/enables it, avoiding noisy 400s
	 * from the optional certificates/template.svg object when it does not exist.
 */
 async loadTemplate(): Promise<void> {
 try {
		 const customTemplateUrl = this.getCustomTemplateUrl();
	 const templateUrls = [
		 ...(customTemplateUrl ? [{ url: customTemplateUrl, source: 'custom certificate template' }] : []),
	 { url: SVGCertificateGenerator.LOCAL_TEMPLATE_URL, source: 'public/certificates/certificate-template.svg' },
	 { url: SVGCertificateGenerator.REMOTE_TEMPLATE_URL, source: 'assets/Aureus_Template.svg' }
	 ];

	 for (const template of templateUrls) {
 try {
	 const response = await fetch(template.url, { cache: 'no-cache' });
	 if (!response.ok) {
	 continue;
	 }
	 const svgContent = await response.text();

	 // Validate SVG content — remote might return an HTML error page
	 if (!this.isValidSvg(svgContent)) {
	 console.warn(`Invalid certificate SVG content received from ${template.source}`);
	 continue;
 }

	 // Clean up any malformed path data injected by browser extensions (Google Translate, wallets, etc.)
	 const cleanedSvg = this.sanitizeSvgPaths(svgContent);

	 this.svgTemplate = cleanedSvg;
	 console.log(`SVG template loaded successfully from ${template.source} (${(cleanedSvg.length / 1024).toFixed(0)} KB)`);
	 return;
	 } catch {
	 // Network error — silently continue to the next bundled/remote fallback.
	 }
 }

	 throw new Error('No certificate SVG template source was available');
 } catch (error) {
	 console.warn('Certificate SVG templates unavailable, using embedded fallback:', error instanceof Error ? error.message : error);
 // Fallback to embedded template
 this.svgTemplate = this.getEmbeddedTemplate();
 console.log('Using fallback embedded template');
 }
 }

		 private getCustomTemplateUrl(): string | null {
		 if (typeof window === 'undefined') return null;
	 try {
		 if (window.localStorage.getItem(SVGCertificateGenerator.CUSTOM_TEMPLATE_FLAG) !== 'true') return null;
		 const rawUrl = window.localStorage.getItem(SVGCertificateGenerator.CUSTOM_TEMPLATE_URL_KEY);
		 if (!rawUrl) return null;
		 const parsed = new URL(rawUrl, window.location.origin);
		 if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
		 return parsed.toString();
	 } catch {
		 return null;
	 }
	 }

	 private isValidSvg(svgContent: string): boolean {
	 return Boolean(svgContent && svgContent.includes('<svg') && svgContent.includes('</svg>'));
	 }

 /**
 * Load the pre-rendered PNG background image for certificate generation.
 * This replaces the 15MB SVG template rendering with a pre-rendered 2.4MB PNG,
 * avoiding browser Image() rendering failures with complex SVGs.
 */
 private async loadBackgroundImage(): Promise<void> {
 if (this.backgroundLoaded) return;
	 const backgroundUrls = [
	 SVGCertificateGenerator.LOCAL_PNG_BACKGROUND_URL,
	 SVGCertificateGenerator.REMOTE_PNG_BACKGROUND_URL
	 ];

	 for (const backgroundUrl of backgroundUrls) {
	 try {
 const img = new Image();
 img.crossOrigin = 'anonymous';
 await new Promise<void>((resolve, reject) => {
 const timeout = setTimeout(() => {
 reject(new Error('Background PNG load timed out after 20s'));
 }, 20000);
 img.onload = () => {
 clearTimeout(timeout);
 this.backgroundImage = img;
 this.backgroundLoaded = true;
 console.log(`Certificate background PNG loaded: ${img.width}x${img.height}`);
 resolve();
 };
 img.onerror = (_e) => {
 clearTimeout(timeout);
	 reject(new Error(`Failed to load certificate background PNG from ${backgroundUrl}`));
 };
	 img.src = backgroundUrl;
 });
	 return;
 } catch (error) {
	 console.warn('Failed to load PNG background candidate:', error instanceof Error ? error.message : error);
 this.backgroundImage = null;
 this.backgroundLoaded = false;
 }
	 }

	 console.warn('Failed to load all PNG backgrounds, will fall back to SVG pipeline');
 }

 /**
 * Sanitize SVG path data — removes malformed commands injected by browser extensions
 * Handles tc, sc, and other invalid SVG path command patterns
 */
 private sanitizeSvgPaths(svgContent: string): string {
 let cleaned = svgContent;

 // Pattern 1: Standalone malformed commands within path d="" attributes (tc0.2, sc0.3, etc.)
 // These are injected by Google Translate and other browser extensions
 cleaned = cleaned.replace(/d="([^"]*)"/g, (_match, pathData: string) => {
 // Remove any invalid SVG path commands: tc, sc followed by numbers
 let cleanedPath = pathData.replace(/\s*[ts]c[\d.,-]+(\s+[\d.,-]+)*/gi, '');
	 // Normalize coordinate runs like "0.4-0.2" to "0.4 -0.2" for stricter SVG parsers.
	 cleanedPath = cleanedPath.replace(/([0-9.])-([0-9.])/g, '$1 -$2');
 // Remove any remaining orphaned commas or spaces from cleanup
 cleanedPath = cleanedPath.replace(/\s{2,}/g, ' ').trim();
 // If the entire path was invalidated, replace with a safe no-op path
 if (!cleanedPath || !/[MLHVCSQTAZ]/i.test(cleanedPath)) {
 return 'd="M 0 0"';
 }
 return `d="${cleanedPath}"`;
 });

 return cleaned;
 }

 /**
 * Load certificate template elements from database
 */
 async loadTemplateElements(templateVersion: string = 'v1.0'): Promise<CertificateTemplateElement[]> {
 try {
 // Use API endpoint - NO direct supabase in browser
 const result = await callCertificateAPI('getTemplateElements', { templateVersion });

 if (result.error) {
 console.error('Error loading template elements:', result.error);
 return [];
 }

 return result.data || [];
 } catch (error) {
 console.error('Error loading template elements:', error);
 return [];
 }
 }



 /**
 * Decrypt ID number from encrypted storage
 */
 private decryptIdNumber(encryptedId: string | null | undefined): string {
 try {
 if (!encryptedId) return 'Not provided';
 // The ID is base64 encoded, so we decode it
 return atob(encryptedId);
 } catch (error) {
 console.error('Error decrypting ID number:', error);
 return 'Not provided';
 }
 }

 /**
 * Get KYC data for user
 */
 private async getKYCData(userId: number): Promise<KYCData | null> {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callCertificateAPI('getKycData', { userId });

 if (result.error) {
 console.error('Error fetching KYC data:', result.error);
 return null;
 }

 return result.data || null;
 } catch (error) {
 console.error('Error fetching KYC data:', error);
 return null;
 }
 }

 /**
 * Get user data
 */
 private async getUserData(userId: number): Promise<any> {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callCertificateAPI('getUserFullDetails', { userId });

 if (result.error) throw new Error(result.error);
 return result.data;
 } catch (error) {
 console.error('Error fetching user data:', error);
 return null;
 }
 }

 /**
 * Resolve address fields for a certificate, preferring:
 * 1. Purchase-specific overrides (certificate_address_overrides)
 * 2. User-level overrides (user_certificate_address_overrides)
 * 3. KYC + user profile data as fallback
 */
 private async getAddressForCertificate(options: {
 userId: number;
 purchaseId?: string;
 kycData?: any;
 userData?: any;
 }): Promise<{
 line1: string;
 line2: string;
 city: string;
 country: string;
 postalCode: string;
 }> {
 const { userId, purchaseId, kycData, userData } = options;

 let override: any | null = null;

 // Use API endpoint - NO getServiceRoleClient() in browser
 try {
 const result = await callCertificateAPI('getPurchaseOverride', { purchaseId, userId });
 if (result.data) {
 override = result.data;
 }
 } catch (error) {
 console.error('Error loading address override:', error);
 }

 const baseKyc = kycData ?? (await this.getKYCData(userId));
 const baseUser = userData ?? (await this.getUserData(userId));

 const fallbackLine1 =
 baseKyc?.street_address || baseUser?.country_of_residence || 'Address not provided';
 // Include province in line2 for proper address display
 const fallbackLine2 = baseKyc?.province || baseKyc?.suburb || '';

 return {
 line1: override?.address_line1 ?? fallbackLine1,
 line2: override?.address_line2 ?? fallbackLine2,
 city: override?.city ?? baseKyc?.city ?? '',
 country:
 override?.country ?? baseKyc?.country_name ?? baseUser?.country_of_residence ?? '',
 postalCode: override?.postal_code ?? baseKyc?.postal_code ?? ''
 };
 }


 /**
 * Generate certificate data from purchase
 */
 async generateCertificateData(purchaseId: string): Promise<CertificateData | null> {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const purchaseResult = await callCertificateAPI('getPurchaseById', { purchaseId });

 if (purchaseResult.error) {
 if (purchaseResult.error.includes('PGRST116') || !purchaseResult.data) {
 throw new Error(`Purchase record not found: ${purchaseId}. This may be an orphaned certificate.`);
 }
 throw new Error(purchaseResult.error);
 }

 const purchase = purchaseResult.data;
 if (!purchase) {
 throw new Error(`Purchase record not found: ${purchaseId}`);
 }

 // Get user and KYC data
 const userData = await this.getUserData(purchase.user_id);
 const kycData = await this.getKYCData(purchase.user_id);

 if (!userData) {
 throw new Error('User data not found');
 }

 // Use API to create certificate with dual numbering system
 const certResult = await callCertificateAPI('createCertificateDual', {
 userId: purchase.user_id,
 purchaseId: purchaseId,
 sharesCount: purchase.shares_purchased,
 certificateData: {
 package_name: purchase.package_name,
 total_amount: purchase.total_amount,
 created_by: 'svg_generator'
 }
 });

 if (certResult.error) {
 console.error('Error creating certificate with dual numbering:', certResult.error);
 throw new Error(certResult.error);
 }

 const certNumbers = certResult.data;

 const certNo = certNumbers[0]?.cert_no;

 if (!certNo) {
 throw new Error('Failed to generate certificate number');
 }

 // Resolve address, preferring any certificate-specific override
 const address = await this.getAddressForCertificate({
 userId: purchase.user_id,
 purchaseId,
 kycData,
 userData
 });

 // Decrypt ID number from encrypted storage
 const decryptedIdNumber = this.decryptIdNumber(kycData?.id_number_encrypted);

 return {
 certificateNumber: certNo, // Use certNo for backward compatibility
 certNo: certNo, // Sequential certificate number (e.g., "0-000-007")
 userName:
 kycData?.full_legal_name ||
 (kycData?.first_name && kycData?.last_name
 ? `${kycData.first_name} ${kycData.last_name}`
 : null) ||
 userData.full_name ||
 userData.username,
 userIdNumber: decryptedIdNumber,
 userAddress: {
 line1: address.line1,
 line2: address.line2,
 city: address.city,
 country: address.country,
 postalCode: address.postalCode
 },
 sunId: userData.id.toString(),
 userId: userData.id,
 sharesCount: purchase.shares_purchased,
 issueDate: new Date().toLocaleDateString('en-GB'),
 purchaseDate: new Date(purchase.created_at).toLocaleDateString('en-GB')
 };
 } catch (error) {
 console.error('Error generating certificate data:', error);
 return null;
 }
 }

 /**
 * Generate certificate and return PNG blob for download
 */
 async generateCertificatePNG(purchaseId: string, saveToDatabase: boolean = false): Promise<{
 pngBlob: Blob | null;
 certificateData: CertificateData | null;
 pngUrl?: string;
 }> {
 try {
 const certData = await this.generateCertificateData(purchaseId);
 if (!certData) {
 throw new Error('Failed to generate certificate data');
 }

 // PRIMARY: PNG background approach (fast, reliable, ornate design)
 let pngBlob = await this.renderCertificateFromPNGBackground(certData);

 // FALLBACK: Old SVG pipeline if PNG background failed
 if (!pngBlob) {
 console.warn('PNG background rendering failed, falling back to SVG pipeline...');
 if (!this.svgTemplate) {
 await this.loadTemplate();
 }
 let svgCertificate = this.svgTemplate;
 const userIdFromSun = Number.parseInt(certData.sunId, 10);
 const maybeUserId = Number.isNaN(userIdFromSun) ? undefined : userIdFromSun;
 svgCertificate = await this.addDynamicTextOverlays(svgCertificate, certData, maybeUserId);
 pngBlob = await this.svgToHighQualityPNG(svgCertificate);
 }

 if (!pngBlob) {
 throw new Error('Failed to convert certificate to PNG (both pipelines failed)');
 }

 let pngUrl: string | undefined;

 // Save to database if requested
 if (saveToDatabase) {
	 pngUrl = (await this.saveCertificateToDatabase(purchaseId, certData, pngBlob)) || undefined;
 }

 return {
 pngBlob,
 certificateData: certData,
 pngUrl
 };
 } catch (error) {
 console.error('Error generating certificate PNG:', error);
 return {
 pngBlob: null,
 certificateData: null
 };
 }
 }

 /**
 * Generate SVG certificate for preview only (not saved, no counters or DB inserts)
 */
 async generateCertificatePreview(purchaseId: string): Promise<string | null> {
 try {
 if (!this.svgTemplate) {
 await this.loadTemplate();
 }

 // Use API endpoint - NO getServiceRoleClient() in browser
 const purchaseResult = await callCertificateAPI('getPurchaseById', { purchaseId });

 if (purchaseResult.error) {
 if (purchaseResult.error.includes('PGRST116') || !purchaseResult.data) {
 throw new Error(`Purchase record not found for preview: ${purchaseId}`);
 }
 throw new Error(purchaseResult.error);
 }

 const purchase = purchaseResult.data;
 if (!purchase) {
 throw new Error(`Purchase record not found for preview: ${purchaseId}`);
 }

 const userData = await this.getUserData(purchase.user_id);
 const kycData = await this.getKYCData(purchase.user_id);

 if (!userData) {
 throw new Error('User data not found for preview');
 }

 // Resolve address, preferring any certificate-specific override
 const address = await this.getAddressForCertificate({
 userId: purchase.user_id,
 purchaseId,
 kycData,
 userData
 });

 // Decrypt ID number for display
 const decryptedIdNumber = this.decryptIdNumber(kycData?.id_number_encrypted);

 // Use clear placeholders for numbers so preview does not reserve real certificate numbers
 const previewCertificateNumber = 'PREVIEW-ONLY';

 const certData: CertificateData = {
 certificateNumber: previewCertificateNumber,
 certNo: previewCertificateNumber,
 userName:
 kycData?.full_legal_name ||
 (kycData?.first_name && kycData?.last_name
 ? `${kycData.first_name} ${kycData.last_name}`
 : null) ||
 userData.full_name ||
 userData.username,
 userIdNumber: decryptedIdNumber,
 userAddress: {
 line1: address.line1,
 line2: address.line2,
 city: address.city,
 country: address.country,
 postalCode: address.postalCode
 },
 sunId: userData.id.toString(),
 userId: userData.id,
 sharesCount: purchase.shares_purchased,
 issueDate: new Date().toLocaleDateString('en-GB'),
 purchaseDate: new Date(purchase.created_at).toLocaleDateString('en-GB')
 };

 // Replace placeholders in SVG template
 let certificate = this.svgTemplate;

 // Add text overlays to the existing beautiful template, including per-user overrides
 const userIdFromSun = Number.parseInt(certData.sunId, 10);
 const maybeUserId = Number.isNaN(userIdFromSun) ? undefined : userIdFromSun;
 certificate = await this.addDynamicTextOverlays(certificate, certData, maybeUserId);

 return certificate;
 } catch (error) {
 console.error('Error generating certificate preview:', error);
 return null;
 }
 }

 /**
 * Generate SVG certificate preview for a user (without needing a specific purchase).
 * Used by admin to preview layout before generating consolidated certificates.
 */
 async generateCertificatePreviewForUser(userId: number, sharesCount?: number): Promise<string | null> {
 try {
 if (!this.svgTemplate) {
 await this.loadTemplate();
 }

 // Use API endpoint - NO getServiceRoleClient() in browser
 const userData = await this.getUserData(userId);
 const kycData = await this.getKYCData(userId);

 if (!userData) {
 throw new Error('User data not found for preview');
 }

 // If no shares count provided, get total shares owned via API
 let totalShares = sharesCount;
 if (!totalShares) {
 const sharesResult = await callCertificateAPI('getUserTotalShares', { userId });
 totalShares = sharesResult.data || 0;
 }

 // Get address from KYC
 const address = await this.getAddressForCertificate({
 userId,
 kycData,
 userData
 });

 // Decrypt ID number for display
 const decryptedIdNumber = this.decryptIdNumber(kycData?.id_number_encrypted);

 // Use clear placeholders for numbers so preview does not reserve real certificate numbers
 const previewCertificateNumber = 'PREVIEW-ONLY';

 const certData: CertificateData = {
 certificateNumber: previewCertificateNumber,
 certNo: previewCertificateNumber,
 userName:
 kycData?.full_legal_name ||
 (kycData?.first_name && kycData?.last_name
 ? `${kycData.first_name} ${kycData.last_name}`
 : null) ||
 userData.full_name ||
 userData.username,
 userIdNumber: decryptedIdNumber,
 userAddress: {
 line1: address.line1,
 line2: address.line2,
 city: address.city,
 country: address.country,
 postalCode: address.postalCode
 },
 sunId: userData.id.toString(),
 userId: userData.id,
 sharesCount: Math.floor(totalShares ?? 0),
 issueDate: new Date().toLocaleDateString('en-GB'),
 purchaseDate: new Date().toLocaleDateString('en-GB')
 };

 // Replace placeholders in SVG template
 let certificate = this.svgTemplate;

 // Add text overlays to the existing beautiful template, including per-user overrides
 certificate = await this.addDynamicTextOverlays(certificate, certData, userId);

 return certificate;
 } catch (error) {
 console.error('Error generating certificate preview for user:', error);
 return null;
 }
 }


 /**
 * Regenerate PNG for an existing certificate without creating a new certificate record.
 * Used when a certificate row already exists but its PNG file is missing or needs refresh.
 */
 async regenerateExistingCertificatePNG(params: {
 certificateNumber: string;
 sharesCount: number;
 purchaseId: string;
 userId: number;
 issueDate?: string | null;
 }): Promise<{ pngBlob: Blob | null; certificateData: CertificateData | null; pngUrl?: string }> {
 try {
 if (!this.svgTemplate) {
 await this.loadTemplate();
 }

 // Use API endpoint - NO getServiceRoleClient() in browser
 const purchaseResult = await callCertificateAPI('getPurchaseById', { purchaseId: params.purchaseId });

 if (purchaseResult.error) {
 if (purchaseResult.error.includes('PGRST116') || !purchaseResult.data) {
 throw new Error(`Purchase record not found for existing certificate: ${params.purchaseId}`);
 }
 throw new Error(purchaseResult.error);
 }

 const purchase = purchaseResult.data;
 if (!purchase) {
 throw new Error(`Purchase record not found for existing certificate: ${params.purchaseId}`);
 }

 const userData = await this.getUserData(params.userId);
 const kycData = await this.getKYCData(params.userId);

 if (!userData) {
 throw new Error('User data not found for existing certificate');
 }

 const address = await this.getAddressForCertificate({
 userId: params.userId,
 purchaseId: params.purchaseId,
 kycData,
 userData
 });

 const decryptedIdNumber = this.decryptIdNumber(kycData?.id_number_encrypted);

 const certData: CertificateData = {
 certificateNumber: params.certificateNumber,
 certNo: params.certificateNumber,
 userName:
 kycData?.full_legal_name ||
 (kycData?.first_name && kycData?.last_name
 ? `${kycData.first_name} ${kycData.last_name}`
 : null) ||
 userData.full_name ||
 userData.username,
 userIdNumber: decryptedIdNumber,
 userAddress: {
 line1: address.line1,
 line2: address.line2,
 city: address.city,
 country: address.country,
 postalCode: address.postalCode
 },
 sunId: userData.id.toString(),
 userId: userData.id,
 sharesCount: params.sharesCount,
 issueDate: params.issueDate
 ? new Date(params.issueDate).toLocaleDateString('en-GB')
 : new Date().toLocaleDateString('en-GB'),
 purchaseDate: new Date(purchase.created_at).toLocaleDateString('en-GB')
 };

	 // PRIMARY: PNG background approach (fast, reliable, avoids complex SVG render failures)
	 let pngBlob = await this.renderCertificateFromPNGBackground(certData);

	 // FALLBACK: SVG pipeline if PNG background is unavailable
	 if (!pngBlob) {
	 console.warn('PNG background rendering failed for existing certificate, falling back to SVG pipeline...');
	 let svgCertificate = this.svgTemplate;
	 const userIdFromSun = Number.parseInt(certData.sunId, 10);
	 const maybeUserId = Number.isNaN(userIdFromSun) ? undefined : userIdFromSun;
	 svgCertificate = await this.addDynamicTextOverlays(svgCertificate, certData, maybeUserId);
	 pngBlob = await this.svgToHighQualityPNG(svgCertificate);
	 }
 if (!pngBlob) {
 throw new Error('Failed to convert existing certificate to PNG');
 }

 // Upload PNG (upsert) using the existing certificate number
 const pngUrl = await this.uploadCertificatePNG(params.certificateNumber, params.userId, pngBlob);
 if (!pngUrl) {
 throw new Error('Failed to upload regenerated certificate PNG to storage');
 }

 // Generate QR code URL and persist it on the existing certificate row
 const qrCodeURL = this.generateCertificateQRCodeURL(params.certificateNumber, params.userId);

 // Use API endpoint to get existing certificate data - NO getServiceRoleClient() in browser
 const certDataResult = await callCertificateAPI('getCertificateDataByNumber', {
 certificateNumber: params.certificateNumber
 });

 if (certDataResult.error) {
 console.warn('Warning: Failed to fetch existing certificate_data for update:', certDataResult.error);
 }

 const existingData = certDataResult.data || {};
 const updatedCertificateData = {
 ...existingData,
 file_url: pngUrl,
 qr_code_url: qrCodeURL,
 file_format: 'PNG',
 last_regenerated_at: new Date().toISOString()
 };

 // Use API endpoint to update certificate - NO getServiceRoleClient() in browser
 const updateResult = await callCertificateAPI('updateCertificateByNumber', {
 certificateNumber: params.certificateNumber,
 updates: {
 certificate_data: updatedCertificateData,
 qr_code_url: qrCodeURL
 }
 });

 if (!updateResult.success) {
 console.warn('Warning: Failed to update existing certificate record with PNG URL:', updateResult.error);
 }

 return {
 pngBlob,
 certificateData: certData,
 pngUrl
 };
 } catch (error) {
 console.error('Error regenerating existing certificate PNG:', error);
 return {
 pngBlob: null,
 certificateData: null,
 pngUrl: undefined
 };
 }
 }

 /**
 * Generate SVG certificate for test purposes with custom data
 */
 async generateTestCertificate(testData: {
 userName: string;
 userIdNumber: string;
 addressLine1: string;
 addressLine2: string;
 sharesCount: number;
 certificateNumber: string;
 sunId: string;
 issueDate: string;
 }): Promise<string | null> {
 try {
 if (!this.svgTemplate) {
 await this.loadTemplate();
 }

 // Replace placeholders in SVG template
 let certificate = this.svgTemplate;

 // Create certificate data object for overlay
 const certData: CertificateData = {
 certificateNumber: testData.certificateNumber, // Backward compatibility
 certNo: testData.certificateNumber, // Use same for test
 userName: testData.userName,
 userIdNumber: testData.userIdNumber,
 userAddress: {
 line1: testData.addressLine1,
 line2: testData.addressLine2,
 city: '',
 country: '',
 postalCode: ''
 },
 sunId: testData.sunId,
 userId: 0,
 sharesCount: testData.sharesCount,
 issueDate: testData.issueDate,
 purchaseDate: testData.issueDate
 };

 // Add text overlays to the existing beautiful template (no user overrides in test mode)
 certificate = await this.addDynamicTextOverlays(certificate, certData, undefined);

 return certificate;
 } catch (error) {
 console.error('Error generating test certificate:', error);
 return null;
 }
 }

 /**
 * Generate a consolidated certificate PNG for self-regeneration (no purchase_id required)
 * Used when users regenerate their own certificates after share count changes
 */
 async generateConsolidatedCertificatePNG(params: {
 userId: number;
 certificateNumber: string;
 sharesCount: number;
 userName: string;
 userIdNumber: string;
 addressLine1: string;
 addressLine2: string;
 city: string;
 country: string;
 postalCode: string;
 issueDate: string;
 }): Promise<{ pngBlob: Blob | null; pngUrl: string | null }> {
 try {
 // Create certificate data object
 const certData: CertificateData = {
 certificateNumber: params.certificateNumber,
 certNo: params.certificateNumber,
 userName: params.userName,
 userIdNumber: params.userIdNumber,
 userAddress: {
 line1: params.addressLine1,
 line2: params.addressLine2,
 city: params.city,
 country: params.country,
 postalCode: params.postalCode
 },
 sunId: params.userId.toString(),
 userId: params.userId,
 sharesCount: params.sharesCount,
 issueDate: params.issueDate,
 purchaseDate: params.issueDate
 };

 // PRIMARY: PNG background approach (fast, reliable, ornate design)
 let pngBlob = await this.renderCertificateFromPNGBackground(certData);

 // FALLBACK: Old SVG pipeline if PNG background failed
 if (!pngBlob) {
 console.warn('PNG background rendering failed, falling back to SVG pipeline...');
 if (!this.svgTemplate) {
 await this.loadTemplate();
 }
 let certificate = this.svgTemplate;
 certificate = await this.addDynamicTextOverlays(certificate, certData, params.userId);
 pngBlob = await this.svgToHighQualityPNG(certificate);
 }

 if (!pngBlob) {
 throw new Error('Failed to convert certificate to PNG (both PNG background and SVG pipelines failed)');
 }

 // Upload PNG to storage
 const pngUrl = await this.uploadCertificatePNG(
 params.certificateNumber,
 params.userId,
 pngBlob
 );

 if (!pngUrl) {
 throw new Error('Failed to upload certificate PNG to storage');
 }

 // Generate QR code URL
 const qrCodeURL = this.generateCertificateQRCodeURL(params.certificateNumber, params.userId);

 // Use API endpoint to update certificate - NO getServiceRoleClient() in browser
 const updateResult = await callCertificateAPI('updateCertificateByNumber', {
 certificateNumber: params.certificateNumber,
 updates: {
 certificate_data: {
 file_url: pngUrl,
 qr_code_url: qrCodeURL,
 file_format: 'PNG',
 created_by: 'svg_generator',
 generated_at: new Date().toISOString(),
 self_regenerated: true
 },
 qr_code_url: qrCodeURL
 }
 });

 if (!updateResult.success) {
 console.warn('Warning: Failed to update certificate record with PNG URL:', updateResult.error);
 }

 console.log('Consolidated certificate PNG generated and uploaded:', pngUrl);
 return { pngBlob, pngUrl };
 } catch (error) {
 console.error('Error generating consolidated certificate PNG:', error);
 return { pngBlob: null, pngUrl: null };
 }
 }

 /**
 * Save certificate to database with PNG file
 */
 private async saveCertificateToDatabase(
 purchaseId: string,
 certData: CertificateData,
	 pngBlob: Blob
 ): Promise<string | null> {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const purchaseResult = await callCertificateAPI('getPurchaseById', { purchaseId });

 if (purchaseResult.error) throw new Error(purchaseResult.error);
 const purchase = purchaseResult.data;
 if (!purchase) throw new Error('Purchase not found');

 // Upload PNG to storage
 const pngUrl = await this.uploadCertificatePNG(
 certData.certificateNumber,
 purchase.user_id,
 pngBlob
 );

 if (!pngUrl) {
 throw new Error('Failed to upload certificate PNG to storage');
 }

 // Generate permanent QR code URL
 const qrCodeURL = this.generateCertificateQRCodeURL(certData.certificateNumber, purchase.user_id);

 // Use API endpoint to create certificate record - NO getServiceRoleClient() in browser
 const createResult = await callCertificateAPI('createPngCertificate', {
 userId: purchase.user_id,
 purchaseId: purchaseId,
 certificateNumber: certData.certificateNumber,
 sharesCount: certData.sharesCount,
 pngUrl: pngUrl,
 fileSize: pngBlob.size,
 certificateData: {
 sun_id: certData.sunId,
 issue_date: certData.issueDate,
 purchase_date: certData.purchaseDate,
 user_address: certData.userAddress,
 generation_timestamp: new Date().toISOString(),
 file_format: 'PNG',
 dpi: 300,
 dimensions: '3000x2121', // 1000x707 at 300 DPI
 qr_code_url: qrCodeURL // Store QR code URL in certificate data
 }
 });

 if (createResult.error) {
 console.error('Error saving certificate to database:', createResult.error);
 throw new Error(createResult.error);
 }

 // Update the certificate record with QR code URL
 const updateResult = await callCertificateAPI('updateCertificateByNumber', {
 certificateNumber: certData.certificateNumber,
 updates: { qr_code_url: qrCodeURL }
 });

 if (!updateResult.success) {
 console.warn('Warning: Failed to update QR code URL:', updateResult.error);
 // Don't fail the entire operation if QR code URL update fails
 }

 console.log('Certificate PNG saved to database with QR code:', createResult.data);
 return pngUrl;
 } catch (error) {
 console.error('Error saving certificate to database:', error);
 throw error;
 }
 }

 /**
 * Render a certificate by drawing the pre-rendered PNG background onto a canvas
 * and overlaying dynamic text fields + QR code directly via the Canvas 2D API.
 * This avoids the 15MB SVG → Image() rendering bottleneck entirely.
 */
 private async renderCertificateFromPNGBackground(certData: CertificateData): Promise<Blob | null> {
 try {
 // Ensure background image is loaded
 if (!this.backgroundImage) {
 await this.loadBackgroundImage();
 }
 if (!this.backgroundImage) {
 console.warn('PNG background image not available');
 return null;
 }

 const bgImg = this.backgroundImage;
 const canvas = document.createElement('canvas');
 canvas.width = bgImg.width;
 canvas.height = bgImg.height;
 const ctx = canvas.getContext('2d');
 if (!ctx) throw new Error('Canvas context not available');

 // Scale factor: canvas pixels per SVG viewBox unit
 const scaleX = canvas.width / SVGCertificateGenerator.SVG_VIEWBOX_WIDTH;
 const scaleY = canvas.height / SVGCertificateGenerator.SVG_VIEWBOX_HEIGHT;

 // 1. Draw the background image
 ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);

 // 2. Enable high-quality text rendering
 ctx.imageSmoothingEnabled = true;
 ctx.imageSmoothingQuality = 'high';
 ctx.textBaseline = 'alphabetic';

 // 3. Load template elements from database for text positions
 const elements = await this.loadTemplateElements();

 // Helper: map SVG coordinates to canvas coordinates
 const mapX = (x: number) => x * scaleX;
 const mapY = (y: number) => y * scaleY;
 const mapFontSize = (size: number) => size * scaleX; // scale uniformly

 // Helper: get dynamic content for an element
 const getContent = (el: CertificateTemplateElement): string => {
 if (!el.is_dynamic) return el.static_content || '';
 switch (el.data_field) {
 case 'certificateNumber': return certData.certificateNumber || '';
 case 'userName': return certData.userName || '';
 case 'userIdNumber': return `ID: ${certData.userIdNumber || ''}`;
 case 'sharesCount': return String(certData.sharesCount ?? '');
 case 'issueDate': return certData.issueDate || '';
 case 'sunId': return `Aureus ID: ${certData.sunId || ''}`;
 case 'refNumber': return certData.certificateNumber || '';
 case 'userAddress': return ''; // handled separately
 default: return '';
 }
 };

 // Helper: wrap text to fit width
 const wrapText = (text: string, maxWidthPx: number, fontStr: string): string[] => {
 ctx.font = fontStr;
 const words = text.split(' ');
 const lines: string[] = [];
 let currentLine = '';
 for (const word of words) {
 const testLine = currentLine ? `${currentLine} ${word}` : word;
 if (ctx.measureText(testLine).width <= maxWidthPx) {
 currentLine = testLine;
 } else {
 if (currentLine) lines.push(currentLine);
 currentLine = word;
 }
 }
 if (currentLine) lines.push(currentLine);
 return lines;
 };

 // 4. Draw each text element
 for (const el of elements) {
 if (el.element_type !== 'text') continue;

 const fontWeight = el.font_weight === 'bold' ? 'bold' : 'normal';
 const fontSize = mapFontSize(el.font_size || 12);
 const fontFamily = el.font_family || 'Arial, sans-serif';
 const fontStr = `${fontWeight} ${fontSize}px ${fontFamily}`;
 ctx.font = fontStr;
 ctx.fillStyle = el.font_color || '#000000';

 const x = mapX(parseFloat(String(el.x_position)));
 const y = mapY(parseFloat(String(el.y_position)));
 const maxW = el.width ? mapX(parseFloat(String(el.width))) : undefined;

 // Text anchor alignment
 if (el.text_anchor === 'middle') {
 ctx.textAlign = 'center';
 } else if (el.text_anchor === 'end') {
 ctx.textAlign = 'right';
 } else {
 ctx.textAlign = 'left';
 }

 // Special: address field (multi-line)
 if (el.element_id === 'user_address') {
 const addressParts = [
 certData.userAddress.line1,
 certData.userAddress.line2,
 certData.userAddress.city,
 certData.userAddress.country,
 certData.userAddress.postalCode
 ].filter(part => part && part.trim());
 const fullAddress = `Address: ${addressParts.join(', ') || 'Not provided'}`;
 const lines = wrapText(fullAddress, maxW || mapX(300), fontStr);
 const lineHeight = fontSize * 1.3;
 lines.forEach((line, i) => {
 ctx.fillText(line, x, y + i * lineHeight);
 });
 continue;
 }

 const content = getContent(el);
 if (!content) continue;

 // Wrap if text exceeds element width
 if (maxW && ctx.measureText(content).width > maxW) {
 const lines = wrapText(content, maxW, fontStr);
 const lineHeight = fontSize * 1.3;
 lines.forEach((line, i) => {
 ctx.fillText(line, x, y + i * lineHeight);
 });
 } else {
 ctx.fillText(content, x, y);
 }
 }

 // 5. Draw QR code
 try {
 const qrElement = elements.find(el => el.element_id === 'qr_code');
 if (qrElement) {
 const qrCodeURL = this.generateCertificateQRCodeURL(
 certData.certificateNumber,
 parseInt(certData.sunId) || 0
 );
 // The DB width (e.g. 320) was used in the old SVG pipeline as a generation
 // resolution, NOT the display size. The old code extracted QR internal paths
 // (in module coordinate space ~0-50) and scaled by (dbWidth / 200),
 // giving an effective visual size of ~80 SVG units for dbWidth=320.
 // Replicate that: displaySizeSvg = dbWidth / 4 (≈ dbWidth/200 * ~50 modules)
 const dbWidth = parseFloat(String(qrElement.width || 320));
 const displaySizeSvg = dbWidth / 4; // ~80 SVG units for dbWidth=320
 const qrDisplayPx = mapX(displaySizeSvg);
 // Generate QR at high resolution for clarity, draw at correct display size
 const qrGenSize = Math.max(512, Math.round(qrDisplayPx));
 const qrDataUrl = await QRCode.toDataURL(qrCodeURL, {
 width: qrGenSize,
 margin: 1,
 color: { dark: '#c39a3f', light: '#FFFFFF' },
 errorCorrectionLevel: 'H'
 });
 const qrImg = new Image();
 await new Promise<void>((resolve, reject) => {
 qrImg.onload = () => resolve();
 qrImg.onerror = () => reject(new Error('QR image load failed'));
 qrImg.src = qrDataUrl;
 });
 const qrX = mapX(parseFloat(String(qrElement.x_position)));
 const qrY = mapY(parseFloat(String(qrElement.y_position)));
 ctx.drawImage(qrImg, qrX, qrY, qrDisplayPx, qrDisplayPx);
 console.log(`QR code drawn at (${qrX}, ${qrY}), display ${qrDisplayPx}px (from DB width ${dbWidth}, visual ${displaySizeSvg} SVG units)`);
 }
 } catch (qrError) {
 console.error('Error drawing QR code on canvas:', qrError);
 // Continue without QR code
 }

 // 6. Convert canvas to PNG blob
 return new Promise<Blob | null>((resolve) => {
 canvas.toBlob((blob) => {
 if (blob) {
 console.log(`Certificate PNG generated from PNG background: ${(blob.size / 1024 / 1024).toFixed(2)}MB (${canvas.width}x${canvas.height})`);
 resolve(blob);
 } else {
 console.warn('canvas.toBlob returned null');
 resolve(null);
 }
 }, 'image/png', 1.0);
 });
 } catch (error) {
 console.error('Error in renderCertificateFromPNGBackground:', error);
 return null;
 }
 }

 /**
 * Convert SVG to high-quality PNG (300 DPI)
 * Includes automatic retry with embedded template if the primary SVG fails to render
 */
 async svgToHighQualityPNG(svgString: string): Promise<Blob | null> {
 // First attempt with the provided SVG
 const result = await this.renderSvgToPNG(svgString);
 if (result) return result;

 // If the provided SVG failed (e.g. 15MB template with external refs),
 // retry with the safe embedded template
 console.warn('Primary SVG failed to render as PNG. Retrying with embedded fallback template...');
 const embeddedSvg = this.getEmbeddedTemplate();
 if (embeddedSvg !== svgString) {
 const fallbackResult = await this.renderSvgToPNG(embeddedSvg);
 if (fallbackResult) {
 console.log('Certificate PNG generated using embedded fallback template');
 return fallbackResult;
 }
 }

 console.error('All SVG-to-PNG conversion attempts failed');
 return null;
 }

 /**
 * Internal: render an SVG string to a PNG Blob via Canvas
 */
 private async renderSvgToPNG(svgString: string): Promise<Blob | null> {
 try {
 // Clean up any malformed SVG path data before processing
 const cleanedSvg = this.sanitizeSvgPaths(svgString);

 // Create a canvas element with high DPI
 const canvas = document.createElement('canvas');
 const ctx = canvas.getContext('2d');
 if (!ctx) throw new Error('Canvas context not available');

 // Set canvas dimensions for 300 DPI (3x scale for high quality)
 const scaleFactor = 3; // 300 DPI
 canvas.width = 1000 * scaleFactor;
 canvas.height = 707 * scaleFactor;

 // Enable high-quality rendering
 ctx.imageSmoothingEnabled = true;
 ctx.imageSmoothingQuality = 'high';

 // Create image from SVG
 const img = new Image();
 const svgBlob = new Blob([cleanedSvg], { type: 'image/svg+xml;charset=utf-8' });
 const url = URL.createObjectURL(svgBlob);

 return new Promise((resolve) => {
 // Set a timeout in case the image hangs (large SVG with external refs)
 const timeout = setTimeout(() => {
 URL.revokeObjectURL(url);
 console.warn(`SVG-to-PNG timed out after 15s (SVG size: ${(cleanedSvg.length / 1024).toFixed(0)} KB)`);
 resolve(null);
 }, 15000);

 img.onload = () => {
 clearTimeout(timeout);
 // Fill with white background to ensure no transparency issues
 ctx.fillStyle = '#FFFFFF';
 ctx.fillRect(0, 0, canvas.width, canvas.height);

 // Draw the SVG image at high quality
 ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
 URL.revokeObjectURL(url);

 // Convert to PNG with maximum quality (1.0)
 canvas.toBlob((blob) => {
 if (blob) {
 console.log(`High-quality PNG generated: ${(blob.size / 1024 / 1024).toFixed(2)}MB`);
 resolve(blob);
 } else {
 console.warn('canvas.toBlob returned null');
 resolve(null);
 }
 }, 'image/png', 1.0); // Maximum quality
 };

 img.onerror = (event) => {
 clearTimeout(timeout);
 URL.revokeObjectURL(url);
 console.warn(`Failed to load SVG for PNG conversion (SVG size: ${(cleanedSvg.length / 1024).toFixed(0)} KB)`, event);
 resolve(null); // resolve(null) instead of reject — let caller handle retry
 };

 img.src = url;
 });
 } catch (error) {
 console.error('Error in renderSvgToPNG:', error);
 return null;
 }
 }

 /**
 * Generate a gold-colored QR code as SVG
 */
 async generateGoldQRCode(url: string, size: number = 200): Promise<string> {
 try {
 // Generate QR code as SVG with custom gold color (#c39a3f)
 const qrSvg = await QRCode.toString(url, {
 type: 'svg',
 width: size,
 margin: 1, // Small margin for better scanning
 color: {
 dark: '#c39a3f', // Custom gold color
 light: '#FFFFFF' // White background for visibility
 },
 errorCorrectionLevel: 'H' // High error correction for better scanning
 });

 console.log('QR Code generated successfully, size:', size);
 return qrSvg;
 } catch (error) {
 console.error('Error generating QR code:', error);
 throw error;
 }
 }

 /**
 * Generate permanent QR code URL for a certificate
 * This URL never changes, even if certificate ownership changes
 */
 generateCertificateQRCodeURL(certificateNumber: string, userId: number): string {
 const origin = (typeof window !== 'undefined' && window.location?.origin)
  ? window.location.origin
  : (import.meta.env.VITE_SITE_URL || 'https://www.aureus.africa');
 return `${origin}/verify-certificate/${encodeURIComponent(certificateNumber)}?user=${userId}`;
 }

 /**
 * Upload PNG certificate to Supabase storage
 */
 async uploadCertificatePNG(
 certificateNumber: string,
 userId: number,
 pngBlob: Blob
 ): Promise<string | null> {
 try {
 // Convert Blob to base64 for API transmission - NO getServiceRoleClient() in browser
 const arrayBuffer = await pngBlob.arrayBuffer();
 const base64 = btoa(
 new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
 );

 // Use API endpoint to upload
 const result = await callCertificateAPI('uploadCertificatePngToStorage', {
 certificateNumber,
 userId,
 pngBase64: base64
 });

 if (result.error) {
 console.error('Error uploading certificate PNG:', result.error);
 return null;
 }

 console.log('Certificate PNG uploaded to storage:', result.publicUrl);
 return result.publicUrl;
 } catch (error) {
 console.error('Error uploading certificate PNG:', error);
 return null;
 }
 }

 /**
 * Add dynamic text overlays using database template elements
 */
 private async addDynamicTextOverlays(
 svgContent: string,
 certData: CertificateData,
 userId?: number
 ): Promise<string> {
 // Strip the template's hardcoded {{TOKEN}} placeholder <text> elements before
 // appending the database-driven overlays. These placeholders (e.g. {{USER_NAME}},
 // {{SHARES_COUNT}}) are never string-substituted here; the dynamic overlays are
 // appended on top. Left in place they render as literal tokens beneath the real
 // data in the SVG preview pipeline. This mirrors the PNG canvas pipeline, which
 // draws only the background + overlays and never emits these placeholders.
 svgContent = svgContent.replace(/<text\b[^>]*>[^<]*\{\{[^}]+\}\}[^<]*<\/text>\s*/gi, '');

 // The database template elements (and the SVG_VIEWBOX_* constants) are authored
 // in the 842.25 x 595.5 coordinate space of the issued PNG pipeline. The bundled
 // /public template loads in a 1000 x 707 space, so injecting the DB overlays into
 // it without normalization renders them mis-scaled and clustered to the left.
 // Rebuild the SVG on a canvas that matches the issued-PNG coordinate space and
 // paint the same certificate background beneath, so this SVG preview is a faithful
 // representation of the client-facing PNG rather than the raw /public template.
 svgContent =
 `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" ` +
 `width="${SVGCertificateGenerator.SVG_VIEWBOX_WIDTH}" height="${SVGCertificateGenerator.SVG_VIEWBOX_HEIGHT}" ` +
 `viewBox="0 0 ${SVGCertificateGenerator.SVG_VIEWBOX_WIDTH} ${SVGCertificateGenerator.SVG_VIEWBOX_HEIGHT}" ` +
 `preserveAspectRatio="xMidYMid meet" version="1.0">` +
 `<image href="${SVGCertificateGenerator.REMOTE_PNG_BACKGROUND_URL}" ` +
 `xlink:href="${SVGCertificateGenerator.REMOTE_PNG_BACKGROUND_URL}" x="0" y="0" ` +
 `width="${SVGCertificateGenerator.SVG_VIEWBOX_WIDTH}" height="${SVGCertificateGenerator.SVG_VIEWBOX_HEIGHT}" ` +
 `preserveAspectRatio="xMidYMid slice" />` +
 `</svg>`;

 // Find the closing </svg> tag and insert text elements before it
 const closingSvgIndex = svgContent.lastIndexOf('</svg>');
 if (closingSvgIndex === -1) {
 console.error('Invalid SVG: no closing </svg> tag found');
 return svgContent;
 }

 // Load template elements from database
 const templateElements = await this.loadTemplateElements();
 if (templateElements.length === 0) {
 console.warn('No template elements found, falling back to hardcoded positioning');
 return this.addTextOverlays(svgContent, certData);
 }

 // Merge any per-user layout overrides, if available - NO getServiceRoleClient() in browser
 let elementsToRender = templateElements;

 if (typeof userId === 'number' && Number.isFinite(userId)) {
 try {
 // Use API endpoint for user layout overrides
 const result = await callCertificateAPI('getUserLayoutOverrides', { userId });

 if (result.error) {
 console.error('Error loading user layout overrides:', result.error);
 } else if (result.data && result.data.length > 0) {
 const overrides = result.data;
 const overrideMap = new Map<string, any>();
 overrides.forEach((ov: any) => {
 if (ov.element_id) {
 overrideMap.set(ov.element_id, ov);
 }
 });

 elementsToRender = templateElements.map((el) => {
 const override = overrideMap.get(el.element_id);
 if (!override) return el;

 return {
 ...el,
 x_position: override.x_position ?? el.x_position,
 y_position: override.y_position ?? el.y_position,
 width: override.width ?? el.width,
 height: override.height ?? el.height,
 font_size: override.font_size ?? el.font_size
 };
 });
 }
 } catch (error) {
 console.error('Unexpected error loading user layout overrides:', error);
 }
 }

 // Enhanced helper function to wrap text within a specified width
 const wrapText = (text: string, maxWidth: number, fontSize: number): string[] => {
 if (!text || maxWidth <= 0) return [text || ''];

 const words = text.split(' ');
 const lines: string[] = [];
 let currentLine = '';

 // More accurate character width estimation for Arial font
 // Different characters have different widths, but this is a reasonable average
 const avgCharWidth = fontSize * 0.55; // Slightly more accurate than 0.6

 // Account for some padding/margin
 const usableWidth = maxWidth - (fontSize * 0.2);
 const maxCharsPerLine = Math.floor(usableWidth / avgCharWidth);

 for (const word of words) {
 const testLine = currentLine ? `${currentLine} ${word}` : word;
 const estimatedWidth = testLine.length * avgCharWidth;

 if (estimatedWidth <= usableWidth && testLine.length <= maxCharsPerLine) {
 currentLine = testLine;
 } else {
 if (currentLine) {
 lines.push(currentLine.trim());
 currentLine = word;
 } else {
 // Word is too long for the line, try to break it intelligently
 if (word.length > maxCharsPerLine) {
 // Break long words at reasonable points (commas, hyphens, etc.)
 const breakPoints = [',', '-', '.'];
 let broken = false;

 for (const breakPoint of breakPoints) {
 const breakIndex = word.lastIndexOf(breakPoint, maxCharsPerLine);
 if (breakIndex > 0 && breakIndex < word.length - 1) {
 lines.push(word.substring(0, breakIndex + 1).trim());
 currentLine = word.substring(breakIndex + 1).trim();
 broken = true;
 break;
 }
 }

 if (!broken) {
 // Force break if no natural break point found
 lines.push(word.substring(0, maxCharsPerLine).trim());
 currentLine = word.substring(maxCharsPerLine).trim();
 }
 } else {
 currentLine = word;
 }
 }
 }
 }

 if (currentLine.trim()) {
 lines.push(currentLine.trim());
 }

 // Ensure we don't return empty lines
 return lines.filter(line => line.length > 0);
 };

 // Helper function to get content for an element
 const getElementContent = (element: CertificateTemplateElement): string => {
 if (!element.is_dynamic) {
 return element.static_content || '';
 }

 // Map data fields to certificate data
 switch (element.data_field) {
 case 'certificateNumber':
 return certData.certificateNumber; // Backward compatibility
 case 'certNo':
 return certData.certNo; // New sequential certificate number
 case 'userName':
 return certData.userName;
 case 'userIdNumber':
 return `ID: ${certData.userIdNumber}`;
 case 'userAddress':
 // v4.7.60 forward-only: certificate prints country only (kyc.country_name with
 // users.country_of_residence fallback resolved upstream in getAddressForCertificate).
 // Full street/city/postal still edited and stored in kyc_information for KYC compliance.
 return `Country: ${certData.userAddress.country || 'Not provided'}`;
 case 'sunId':
 return `Aureus ID: ${certData.sunId}`;
 case 'sharesCount':
 return certData.sharesCount.toString();
 // refNumber removed - no longer used on certificates
 case 'issueDate':
 return certData.issueDate;
 default:
 return element.static_content || '';
 }
 };

 // Generate text overlays from template elements
 let textOverlays = '\n <!-- Dynamic text overlays from database template -->\n';

 for (const element of elementsToRender) {
 if (element.element_type !== 'text') continue;

 const content = getElementContent(element);
 if (!content) continue;

 // Handle special cases for multi-line content
 if (element.element_id === 'user_address') {
 // v4.7.60 forward-only: certificate prints country only (was previously the full
 // joined home address: street, province, city, country, postal). Full address is
 // still edited and stored in kyc_information for KYC compliance and Proof of
 // Address verification; the rendered certificate just collapses to country to
 // save layout space. Resolution order: certificate override → user override →
 // kyc_information.country_name → users.country_of_residence (see
 // getAddressForCertificate above).
 const countryValue = certData.userAddress.country?.trim() || 'Not provided';
 // Use the element's actual width for text wrapping (handles long country names
 // like "Democratic Republic of the Congo")
 const elementWidth = element.width || 200;
 const wrappedAddress = wrapText(countryValue, elementWidth, element.font_size || 12);

 // First line with "Country:" prefix
 textOverlays += ` <text x="${element.x_position}" y="${element.y_position}" font-family="${element.font_family}" font-size="${element.font_size}" font-weight="${element.font_weight}" fill="${element.font_color}" text-anchor="${element.text_anchor}">Country: ${wrappedAddress[0] || ''}</text>\n`;

 // Additional lines without prefix (only triggers for very long country names)
 wrappedAddress.slice(1).forEach((line, index) => {
 const lineSpacing = element.font_size ? element.font_size * 1.2 : 12;
 textOverlays += ` <text x="${element.x_position}" y="${element.y_position + lineSpacing + (index * lineSpacing)}" font-family="${element.font_family}" font-size="${element.font_size}" font-weight="${element.font_weight}" fill="${element.font_color}" text-anchor="${element.text_anchor}">${line}</text>\n`;
 });
 } else if (element.width && content.length > 0) {
 // For other elements with width constraints, check if text wrapping is needed
 const elementWidth = element.width;
 const fontSize = element.font_size || 12;
 const estimatedTextWidth = content.length * fontSize * 0.6; // Rough estimate

 if (estimatedTextWidth > elementWidth) {
 // Text is too wide, wrap it
 const wrappedLines = wrapText(content, elementWidth, fontSize);
 wrappedLines.forEach((line, index) => {
 const lineSpacing = fontSize * 1.2;
 textOverlays += ` <text x="${element.x_position}" y="${element.y_position + (index * lineSpacing)}" font-family="${element.font_family}" font-size="${element.font_size}" font-weight="${element.font_weight}" fill="${element.font_color}" text-anchor="${element.text_anchor}">${line}</text>\n`;
 });
 } else {
 // Text fits in one line
 textOverlays += ` <text x="${element.x_position}" y="${element.y_position}" font-family="${element.font_family}" font-size="${element.font_size}" font-weight="${element.font_weight}" fill="${element.font_color}" text-anchor="${element.text_anchor}">${content}</text>\n`;
 }
 } else {
 // Standard single-line text element without width constraints
 textOverlays += ` <text x="${element.x_position}" y="${element.y_position}" font-family="${element.font_family}" font-size="${element.font_size}" font-weight="${element.font_weight}" fill="${element.font_color}" text-anchor="${element.text_anchor}">${content}</text>\n`;
 }
 }

 textOverlays += ' <!-- End dynamic text overlays -->\n';

 // Generate and add QR code
 try {
 console.log('Starting QR code generation...');

 // Find QR code element in template elements
 const qrElement = elementsToRender.find(el => el.element_id === 'qr_code');

 if (!qrElement) {
 console.warn('No QR code element found in template, skipping QR code generation');
 } else {
 // Generate permanent QR code URL (this URL never changes)
 const qrCodeURL = this.generateCertificateQRCodeURL(certData.certificateNumber, parseInt(certData.sunId) || 0);
 console.log('QR Code URL:', qrCodeURL);

 // Get position and size from database element
 const qrX = qrElement.x_position;
 const qrY = qrElement.y_position;
 const qrSize = qrElement.width || 400; // Use width from database, default to 400

 // Generate gold QR code SVG
 const qrSvg = await this.generateGoldQRCode(qrCodeURL, qrSize);
 console.log('QR SVG generated, length:', qrSvg.length);

 // Embed QR code (clean, just the QR code pattern)
 textOverlays += `\n <!-- QR Code for Certificate Verification -->\n`;
 textOverlays += ` <g id="qr-code" transform="translate(${qrX}, ${qrY})">\n`;

 // Parse and embed the QR code SVG content
 const qrSvgMatch = qrSvg.match(/<svg[^>]*>(.*?)<\/svg>/s);
 if (qrSvgMatch && qrSvgMatch[1]) {
 const qrContent = qrSvgMatch[1];
 console.log('QR content extracted, length:', qrContent.length);
 textOverlays += ` <g transform="scale(${qrSize / 200})">\n`; // Scale to fit
 textOverlays += qrContent;
 textOverlays += ` </g>\n`;
 } else {
 console.error('Failed to extract QR code content from SVG');
 }

 textOverlays += ` </g>\n`;
 textOverlays += ` <!-- End QR Code -->\n`;

 console.log('QR code embedded in certificate at position:', qrX, qrY, 'size:', qrSize);
 console.log('QR code URL:', qrCodeURL);
 }
 } catch (error) {
 console.error('Error embedding QR code:', error);
 // Continue without QR code if generation fails
 }

 // Insert the text overlays and QR code before the closing </svg> tag
 return svgContent.slice(0, closingSvgIndex) + textOverlays + svgContent.slice(closingSvgIndex);
 }

 /**
 * Add text overlays to the existing beautiful SVG template (legacy method)
 */
 private addTextOverlays(svgContent: string, certData: CertificateData): string {
 // Find the closing </svg> tag and insert text elements before it
 const closingSvgIndex = svgContent.lastIndexOf('</svg>');
 if (closingSvgIndex === -1) {
 console.error('Invalid SVG: no closing </svg> tag found');
 return svgContent;
 }

 // Helper function to wrap text within a specified width
 const wrapText = (text: string, maxWidth: number, fontSize: number): string[] => {
 const words = text.split(' ');
 const lines: string[] = [];
 let currentLine = '';

 // Approximate character width based on font size (rough estimate)
 const charWidth = fontSize * 0.6;
 const maxCharsPerLine = Math.floor(maxWidth / charWidth);

 for (const word of words) {
 const testLine = currentLine ? `${currentLine} ${word}` : word;
 if (testLine.length <= maxCharsPerLine) {
 currentLine = testLine;
 } else {
 if (currentLine) {
 lines.push(currentLine);
 currentLine = word;
 } else {
 // Word is too long, break it
 lines.push(word);
 }
 }
 }

 if (currentLine) {
 lines.push(currentLine);
 }

 return lines;
 };

 // Wrap the address text to fit in the cell (NAME AND ADDRESS column: x=25 to ~270, width ~245px)
 // Build full address from all parts, then wrap to fit column
 const addressParts = [
 certData.userAddress.line1,
 certData.userAddress.city,
 certData.userAddress.line2, // province
 certData.userAddress.postalCode,
 certData.userAddress.country
 ].filter(Boolean);
 const fullAddress = addressParts.join(', ');
 // Width 200px = ~33 chars per line at font-size 10
 const wrappedAddress = wrapText(fullAddress, 200, 10);

 // Create text overlay elements positioned exactly in the certificate table cells
 const textOverlays = `
 <!-- Text overlays for certificate data positioned in table cells -->

 <!-- Certificate Number (top left) -->
 <text x="97" y="43" font-family="Arial, sans-serif" font-size="11" font-weight="bold" fill="#000000">${certData.certificateNumber}</text>

 <!-- Number of Shares (top right) -->
 <text x="900" y="43" font-family="Arial, sans-serif" font-size="11" font-weight="bold" fill="#000000">${certData.sharesCount}</text>

 <!-- NAME AND ADDRESS cell - positioned in first column (x: 25-270) -->
 <text x="25" y="335" font-family="Arial, sans-serif" font-size="12" fill="#000000">${certData.userName}</text>
 <text x="25" y="350" font-family="Arial, sans-serif" font-size="10" fill="#000000">ID: ${certData.userIdNumber}</text>
 <text x="25" y="365" font-family="Arial, sans-serif" font-size="10" fill="#000000">Address:</text>
 ${wrappedAddress.map((line, index) =>
 `<text x="25" y="${377 + (index * 11)}" font-family="Arial, sans-serif" font-size="10" fill="#000000">${line}</text>`
 ).join('\n ')}
 <text x="25" y="${377 + (wrappedAddress.length * 11) + 8}" font-family="Arial, sans-serif" font-size="10" fill="#000000">Aureus ID: ${certData.sunId}</text>

 <!-- CLASS OF SHARE cell - positioned in second column (x: 270-405) -->
 <text x="337" y="390" font-family="Arial, sans-serif" font-size="9" fill="#000000" text-anchor="middle">NO PAR VALUE</text>
 <text x="337" y="405" font-family="Arial, sans-serif" font-size="9" fill="#000000" text-anchor="middle">SHARES</text>

 <!-- DATE cell - positioned in third column (was fourth, moved left after removing REF NO) -->
 <text x="462" y="400" font-family="Arial, sans-serif" font-size="9" fill="#000000" text-anchor="middle">${certData.issueDate}</text>

 <!-- CERT NO cell - positioned in fifth column (x: 680-840) -->
 <text x="760" y="400" font-family="Arial, sans-serif" font-size="9" fill="#000000" text-anchor="middle">${certData.certificateNumber}</text>

 <!-- NO OF SHARES cell - positioned in sixth column (x: 840-1000) -->
 <text x="920" y="400" font-family="Arial, sans-serif" font-size="9" fill="#000000" text-anchor="middle">${certData.sharesCount}</text>
`;

 // Insert the text overlays before the closing </svg> tag
 return svgContent.slice(0, closingSvgIndex) + textOverlays + svgContent.slice(closingSvgIndex);
 }

 /**
 * Get template with placeholders - creates a version of the beautiful template with placeholders
 */
 private getTemplateWithPlaceholders(): string {
 // This is a modified version of the beautiful template with placeholders added
 return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="1123" zoomAndPan="magnify" viewBox="0 0 842.25 595.499986" height="794" preserveAspectRatio="xMidYMid meet" version="1.0">
 <!-- Beautiful certificate design with placeholders -->
 <defs>
 <filter x="0%" y="0%" width="100%" height="100%" id="9d7ee0dda2">
 <feColorMatrix values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 1 0" color-interpolation-filters="sRGB"/>
 </filter>
 <clipPath id="dfc30fe964">
 <path d="M 0 0.015625 L 841.5 0.015625 L 841.5 594.984375 L 0 594.984375 Z M 0 0.015625 " clip-rule="nonzero"/>
 </clipPath>
 </defs>

 <!-- Background -->
 <g clip-path="url(#dfc30fe964)">
 <path fill="#ffffff" d="M 0 0.015625 L 841.5 0.015625 L 841.5 594.984375 L 0 594.984375 Z M 0 0.015625 " fill-opacity="1" fill-rule="nonzero"/>
 </g>

 <!-- Certificate border and design elements -->
 <rect x="20" y="20" width="802.25" height="555.5" fill="none" stroke="#D4AF37" stroke-width="3"/>
 <rect x="40" y="40" width="762.25" height="515.5" fill="none" stroke="#D4AF37" stroke-width="1"/>

 <!-- Header -->
 <text x="421" y="80" font-family="serif" font-size="36" font-weight="bold" fill="#8B4513" text-anchor="middle">SHARE CERTIFICATE</text>
 <text x="421" y="110" font-family="serif" font-size="18" fill="#2F4F4F" text-anchor="middle">AUREUS ALLIANCE HOLDINGS (PTY) LTD</text>
 <text x="421" y="130" font-family="serif" font-size="14" fill="#2F4F4F" text-anchor="middle">Incorporated in the Republic of South Africa</text>
 <text x="421" y="145" font-family="serif" font-size="14" fill="#2F4F4F" text-anchor="middle">Registration No: 2021/847817/07</text>

 <!-- Certificate Number (top left) -->
 <text x="60" y="60" font-family="Arial" font-size="12" font-weight="bold" fill="#000">Certificate No: {{CERTIFICATE_NUMBER}}</text>

 <!-- Main content area -->
 <text x="421" y="200" font-family="serif" font-size="16" fill="#2F4F4F" text-anchor="middle">This is to certify that</text>

 <!-- User Name -->
 <text x="421" y="240" font-family="serif" font-size="24" font-weight="bold" fill="#8B4513" text-anchor="middle">{{USER_NAME}}</text>

 <!-- ID Number -->
 <text x="421" y="265" font-family="serif" font-size="14" fill="#2F4F4F" text-anchor="middle">ID Number: {{USER_ID_NUMBER}}</text>

 <!-- Address -->
 <text x="421" y="285" font-family="serif" font-size="12" fill="#2F4F4F" text-anchor="middle">{{USER_ADDRESS_LINE1}}</text>
 <text x="421" y="300" font-family="serif" font-size="12" fill="#2F4F4F" text-anchor="middle">{{USER_ADDRESS_LINE2}}</text>

 <!-- Ownership text -->
 <text x="421" y="340" font-family="serif" font-size="16" fill="#2F4F4F" text-anchor="middle">is the registered owner of</text>

 <!-- Share count -->
 <text x="421" y="380" font-family="serif" font-size="28" font-weight="bold" fill="#8B4513" text-anchor="middle">{{SHARES_COUNT}} SHARES</text>
 <text x="421" y="405" font-family="serif" font-size="14" fill="#2F4F4F" text-anchor="middle">ORDINARY PAR VALUE SHARES - $1,000 PER SHARE</text>

 <!-- Bottom section with details -->
 <line x1="80" y1="450" x2="762" y2="450" stroke="#D4AF37" stroke-width="1"/>

 <!-- Reference details -->
 <text x="100" y="480" font-family="Arial" font-size="10" fill="#000">SUN ID: {{SUN_ID}}</text>
 <text x="300" y="480" font-family="Arial" font-size="10" fill="#000">Ref: {{REF_NUMBER}}</text>
 <text x="500" y="480" font-family="Arial" font-size="10" fill="#000">Issue Date: {{ISSUE_DATE}}</text>

 <!-- Signature areas -->
 <text x="150" y="520" font-family="Arial" font-size="10" fill="#000" text-anchor="middle">_________________</text>
 <text x="150" y="535" font-family="Arial" font-size="8" fill="#000" text-anchor="middle">DC James - Director</text>

 <text x="650" y="520" font-family="Arial" font-size="10" fill="#000" text-anchor="middle">_________________</text>
 <text x="650" y="535" font-family="Arial" font-size="8" fill="#000" text-anchor="middle">JP Rademeyer - Director</text>

 <!-- Company seal area -->
 <circle cx="421" cy="520" r="30" fill="none" stroke="#D4AF37" stroke-width="2"/>
 <text x="421" y="520" font-family="Arial" font-size="8" fill="#8B4513" text-anchor="middle">COMPANY</text>
 <text x="421" y="530" font-family="Arial" font-size="8" fill="#8B4513" text-anchor="middle">SEAL</text>
</svg>`;
 }

 /**
 * Embedded SVG template fallback
 */
 private getEmbeddedTemplate(): string {
 return this.getTemplateWithPlaceholders();
 }
}

export const svgCertificateGenerator = new SVGCertificateGenerator();
